import { RaceId, ShipClass } from '../types/entities.js';
/* ════════════════════════════════════════════════════════════
   Terran Ships — Angular industrial, box-like with swept wings
   ════════════════════════════════════════════════════════════ */
const TERRAN_BASE = {
    hullShape: 'angular',
    wingStyle: 'swept',
    cockpitStyle: 'canopy',
    vertexNoiseAmplitude: 0.02,
    vertexNoiseFrequency: 2.0,
    wireframeDensity: 0.3,
    primaryColor: 0x4488cc,
    secondaryColor: 0xaaccee,
    emissiveColor: 0x224466,
    emissiveIntensity: 0.15,
    metalness: 0.7,
    roughness: 0.3,
    opacity: 1.0,
    specialTraits: ['modular-plating', 'utility-hardpoints'],
};
/* ════════════════════════════════════════════════════════════
   Zyr'kath Ships — Organic curves, deformed spheres, tendrils
   ════════════════════════════════════════════════════════════ */
const ZYRKATH_BASE = {
    hullShape: 'organic',
    wingStyle: 'tendrils',
    cockpitStyle: 'eye-cluster',
    vertexNoiseAmplitude: 0.15,
    vertexNoiseFrequency: 3.5,
    wireframeDensity: 0.15,
    primaryColor: 0x44aa22,
    secondaryColor: 0x88dd44,
    emissiveColor: 0x22aa00,
    emissiveIntensity: 0.25,
    metalness: 0.2,
    roughness: 0.8,
    opacity: 1.0,
    specialTraits: ['bio-regeneration', 'acid-spores', 'chitin-armor'],
};
/* ════════════════════════════════════════════════════════════
   Luminara Ships — Crystalline faceted, translucent
   ════════════════════════════════════════════════════════════ */
const LUMINARA_BASE = {
    hullShape: 'crystalline',
    wingStyle: 'fins',
    cockpitStyle: 'crystal-dome',
    vertexNoiseAmplitude: 0.0,
    vertexNoiseFrequency: 0.0,
    wireframeDensity: 0.5,
    primaryColor: 0x88ccff,
    secondaryColor: 0xccffff,
    emissiveColor: 0x88ccff,
    emissiveIntensity: 0.5,
    metalness: 0.9,
    roughness: 0.05,
    opacity: 0.8,
    specialTraits: ['prismatic-refraction', 'light-shields', 'translucent-hull'],
};
/* ════════════════════════════════════════════════════════════
   Vok'ren Ships — Blocky utilitarian, stacked boxes
   ════════════════════════════════════════════════════════════ */
const VOKREN_BASE = {
    hullShape: 'blocky',
    wingStyle: 'panels',
    cockpitStyle: 'slit-viewport',
    vertexNoiseAmplitude: 0.01,
    vertexNoiseFrequency: 1.0,
    wireframeDensity: 0.4,
    primaryColor: 0xcc8844,
    secondaryColor: 0xffcc88,
    emissiveColor: 0x664422,
    emissiveIntensity: 0.1,
    metalness: 0.6,
    roughness: 0.5,
    opacity: 1.0,
    specialTraits: ['cargo-expansion', 'hidden-turrets', 'reinforced-plating'],
};
/* ════════════════════════════════════════════════════════════
   Obsidian Collective — Perfect polyhedra, dark materials
   ════════════════════════════════════════════════════════════ */
const COLLECTIVE_BASE = {
    hullShape: 'geometric',
    wingStyle: 'none',
    cockpitStyle: 'sensor-array',
    vertexNoiseAmplitude: 0.0,
    vertexNoiseFrequency: 0.0,
    wireframeDensity: 0.6,
    primaryColor: 0x333344,
    secondaryColor: 0x666688,
    emissiveColor: 0x4444aa,
    emissiveIntensity: 0.3,
    metalness: 0.95,
    roughness: 0.05,
    opacity: 1.0,
    specialTraits: ['precision-targeting', 'distributed-processing', 'nano-repair'],
};
/* ════════════════════════════════════════════════════════════
   Void Walker — Ethereal, phase-shifting, impossible geometry
   ════════════════════════════════════════════════════════════ */
const VOIDWALKER_BASE = {
    hullShape: 'ethereal',
    wingStyle: 'none',
    cockpitStyle: 'void-lens',
    vertexNoiseAmplitude: 0.08,
    vertexNoiseFrequency: 5.0,
    wireframeDensity: 0.7,
    primaryColor: 0x220044,
    secondaryColor: 0x440088,
    emissiveColor: 0x8800ff,
    emissiveIntensity: 0.7,
    metalness: 0.3,
    roughness: 0.1,
    opacity: 0.6,
    specialTraits: ['phase-shift', 'dimensional-fold', 'reality-distortion', 'glow-aura'],
};
/* ════════════════════════════════════════════════════════════
   Sylphari — Organic seed-pod ships, huge and bio-luminescent
   ════════════════════════════════════════════════════════════ */
const SYLPHARI_BASE = {
    hullShape: 'organic',
    wingStyle: 'tendrils',
    cockpitStyle: 'eye-cluster',
    vertexNoiseAmplitude: 0.12,
    vertexNoiseFrequency: 2.6,
    wireframeDensity: 0.2,
    primaryColor: 0x77cc55,
    secondaryColor: 0xffaadd,
    emissiveColor: 0x55ff88,
    emissiveIntensity: 0.35,
    metalness: 0.1,
    roughness: 0.85,
    opacity: 1.0,
    specialTraits: ['bloom-canopy', 'pollen-cloud', 'rooted-pilot', 'photosynthetic-hull'],
};
/* ════════════════════════════════════════════════════════════
   Krelg Hegemony — Heavy iron war-hulks, oversized
   ════════════════════════════════════════════════════════════ */
const KRELG_BASE = {
    hullShape: 'blocky',
    wingStyle: 'panels',
    cockpitStyle: 'slit-viewport',
    vertexNoiseAmplitude: 0.03,
    vertexNoiseFrequency: 1.2,
    wireframeDensity: 0.55,
    primaryColor: 0x884422,
    secondaryColor: 0xaa6633,
    emissiveColor: 0xff4400,
    emissiveIntensity: 0.2,
    metalness: 0.8,
    roughness: 0.6,
    opacity: 1.0,
    specialTraits: ['ram-prow', 'ironclad-hull', 'overpressured-engines', 'war-banners'],
};
/* ════════════════════════════════════════════════════════════
   Tharix Cabal — Sleek stealth corvettes, matte-black
   ════════════════════════════════════════════════════════════ */
const THARIX_BASE = {
    hullShape: 'angular',
    wingStyle: 'swept',
    cockpitStyle: 'slit-viewport',
    vertexNoiseAmplitude: 0.01,
    vertexNoiseFrequency: 1.0,
    wireframeDensity: 0.35,
    primaryColor: 0x1a1a22,
    secondaryColor: 0x552288,
    emissiveColor: 0xaa22ff,
    emissiveIntensity: 0.25,
    metalness: 0.85,
    roughness: 0.2,
    opacity: 0.95,
    specialTraits: ['stealth-coating', 'signal-jammer', 'masked-silhouette', 'cloak-fins'],
};
/* ════════════════════════════════════════════════════════════
   Chronos Seers — Ringed temporal vessels with halo-engines
   ════════════════════════════════════════════════════════════ */
const CHRONOS_BASE = {
    hullShape: 'crystalline',
    wingStyle: 'fins',
    cockpitStyle: 'crystal-dome',
    vertexNoiseAmplitude: 0.0,
    vertexNoiseFrequency: 0.0,
    wireframeDensity: 0.65,
    primaryColor: 0xddbb77,
    secondaryColor: 0xffeeaa,
    emissiveColor: 0xffcc66,
    emissiveIntensity: 0.55,
    metalness: 0.9,
    roughness: 0.1,
    opacity: 0.9,
    specialTraits: ['time-halo', 'chrono-rings', 'fate-sails', 'slowfield-core'],
};
/* ════════════════════════════════════════════════════════════
   Nova Forgers — Star-forged plasma cruisers, molten core glow
   ════════════════════════════════════════════════════════════ */
const NOVAFORGE_BASE = {
    hullShape: 'geometric',
    wingStyle: 'fins',
    cockpitStyle: 'sensor-array',
    vertexNoiseAmplitude: 0.02,
    vertexNoiseFrequency: 1.5,
    wireframeDensity: 0.5,
    primaryColor: 0xff8833,
    secondaryColor: 0xffcc44,
    emissiveColor: 0xff4400,
    emissiveIntensity: 0.8,
    metalness: 0.75,
    roughness: 0.3,
    opacity: 1.0,
    specialTraits: ['molten-core', 'plasma-vents', 'forge-heat', 'starlight-hull'],
};
/* ════════════════════════════════════════════════════════════
   Abyssal Chorus — Colossal deep-space leviathans, resonant
   ════════════════════════════════════════════════════════════ */
const ABYSSAL_BASE = {
    hullShape: 'ethereal',
    wingStyle: 'none',
    cockpitStyle: 'void-lens',
    vertexNoiseAmplitude: 0.1,
    vertexNoiseFrequency: 4.0,
    wireframeDensity: 0.75,
    primaryColor: 0x110022,
    secondaryColor: 0x332266,
    emissiveColor: 0x5500aa,
    emissiveIntensity: 0.9,
    metalness: 0.5,
    roughness: 0.4,
    opacity: 0.85,
    specialTraits: ['resonance-hymn', 'abyss-song', 'leviathan-scale', 'dark-chorus'],
};
const CLASS_PARAMS = {
    [ShipClass.Scout]: {
        scale: 0.8, length: 1.2, width: 0.8, height: 0.6,
        detail: 1, engineCount: 1, engineSpread: 0, engineScale: 0.6,
    },
    [ShipClass.Fighter]: {
        scale: 1.0, length: 1.4, width: 1.0, height: 0.7,
        detail: 1, engineCount: 2, engineSpread: 0.5, engineScale: 0.7,
    },
    [ShipClass.Interceptor]: {
        scale: 0.9, length: 1.6, width: 0.7, height: 0.5,
        detail: 1, engineCount: 2, engineSpread: 0.4, engineScale: 0.9,
    },
    [ShipClass.Cruiser]: {
        scale: 2.5, length: 2.0, width: 1.3, height: 1.0,
        detail: 2, engineCount: 4, engineSpread: 0.8, engineScale: 1.0,
    },
    [ShipClass.Freighter]: {
        scale: 2.0, length: 2.2, width: 1.8, height: 1.5,
        detail: 1, engineCount: 2, engineSpread: 1.0, engineScale: 1.2,
    },
    [ShipClass.Destroyer]: {
        scale: 3.5, length: 2.5, width: 1.5, height: 1.2,
        detail: 2, engineCount: 6, engineSpread: 1.2, engineScale: 1.0,
    },
    [ShipClass.Mothership]: {
        scale: 12, length: 3.2, width: 2.6, height: 2.2,
        detail: 3, engineCount: 8, engineSpread: 1.8, engineScale: 1.6,
    },
};
function buildEngines(count, spread, engineScale) {
    const engines = [];
    if (count === 1) {
        engines.push({ x: 0, y: 0, z: -1, scale: engineScale });
    }
    else {
        const rows = count <= 2 ? 1 : Math.ceil(count / 2);
        const perRow = Math.ceil(count / rows);
        for (let row = 0; row < rows; row++) {
            const zOff = -1 - row * 0.3;
            for (let i = 0; i < perRow && engines.length < count; i++) {
                const xOff = (i - (perRow - 1) / 2) * spread;
                engines.push({ x: xOff, y: row * -0.15, z: zOff, scale: engineScale });
            }
        }
    }
    return engines;
}
function makeBlueprint(base, params, sizeBonus = 1.0) {
    return {
        ...base,
        scale: params.scale * sizeBonus,
        lengthMultiplier: params.length,
        widthMultiplier: params.width,
        heightMultiplier: params.height,
        geometryDetail: params.detail,
        engines: buildEngines(params.engineCount, params.engineSpread, params.engineScale),
    };
}
function buildRaceBlueprints(base, sizeBonus = 1.0) {
    const result = {};
    for (const sc of [ShipClass.Scout, ShipClass.Fighter, ShipClass.Interceptor, ShipClass.Cruiser, ShipClass.Freighter, ShipClass.Destroyer]) {
        result[sc] = makeBlueprint(base, CLASS_PARAMS[sc], sizeBonus);
    }
    return result;
}
/**
 * Per-race hull-size multipliers. Frontier civilizations build noticeably
 * larger vessels than the core four. Values are applied on top of the class
 * `scale` so ship-class differences remain consistent.
 *
 * Apex civilizations (Eidolon / Astral / Sovereign) are end-game content —
 * they currently reuse the closest thematically matching base blueprint
 * with an outsized hull bonus until each gets its own bespoke mesh
 * package. The visual distinction is meant to come from per-race
 * geometryDetail / engine glow / hull tinting rather than this raw size
 * multiplier alone.
 */
export const RACE_SIZE_BONUS = {
    [RaceId.Terran]: 1.0,
    [RaceId.Zyrkath]: 1.05,
    [RaceId.Luminara]: 1.0,
    [RaceId.Vokren]: 1.05,
    [RaceId.ObsidianCollective]: 1.1,
    [RaceId.VoidWalker]: 1.15,
    [RaceId.Sylphari]: 1.25,
    [RaceId.KrelgHegemony]: 1.4,
    [RaceId.TharixCabal]: 1.1,
    [RaceId.ChronosSeers]: 1.3,
    [RaceId.NovaForgers]: 1.45,
    [RaceId.AbyssalChorus]: 1.75,
    [RaceId.EidolonConclave]: 1.6,
    [RaceId.AstralLeviathan]: 1.9,
    [RaceId.SingularitySovereign]: 2.1,
};
export const SHIP_BLUEPRINTS = {
    [RaceId.Terran]: buildRaceBlueprints(TERRAN_BASE, RACE_SIZE_BONUS[RaceId.Terran]),
    [RaceId.Zyrkath]: buildRaceBlueprints(ZYRKATH_BASE, RACE_SIZE_BONUS[RaceId.Zyrkath]),
    [RaceId.Luminara]: buildRaceBlueprints(LUMINARA_BASE, RACE_SIZE_BONUS[RaceId.Luminara]),
    [RaceId.Vokren]: buildRaceBlueprints(VOKREN_BASE, RACE_SIZE_BONUS[RaceId.Vokren]),
    [RaceId.ObsidianCollective]: buildRaceBlueprints(COLLECTIVE_BASE, RACE_SIZE_BONUS[RaceId.ObsidianCollective]),
    [RaceId.VoidWalker]: buildRaceBlueprints(VOIDWALKER_BASE, RACE_SIZE_BONUS[RaceId.VoidWalker]),
    [RaceId.Sylphari]: buildRaceBlueprints(SYLPHARI_BASE, RACE_SIZE_BONUS[RaceId.Sylphari]),
    [RaceId.KrelgHegemony]: buildRaceBlueprints(KRELG_BASE, RACE_SIZE_BONUS[RaceId.KrelgHegemony]),
    [RaceId.TharixCabal]: buildRaceBlueprints(THARIX_BASE, RACE_SIZE_BONUS[RaceId.TharixCabal]),
    [RaceId.ChronosSeers]: buildRaceBlueprints(CHRONOS_BASE, RACE_SIZE_BONUS[RaceId.ChronosSeers]),
    [RaceId.NovaForgers]: buildRaceBlueprints(NOVAFORGE_BASE, RACE_SIZE_BONUS[RaceId.NovaForgers]),
    [RaceId.AbyssalChorus]: buildRaceBlueprints(ABYSSAL_BASE, RACE_SIZE_BONUS[RaceId.AbyssalChorus]),
    // Apex civilizations: alias to thematically closest core blueprint
    // until the bespoke meshes ship. Eidolon paradox-knights borrow the
    // Chronos glass aesthetic, Astralleviathans and Singularity
    // Sovereigns share the Abyssal "deep-void leviathan" silhouette.
    [RaceId.EidolonConclave]: buildRaceBlueprints(CHRONOS_BASE, RACE_SIZE_BONUS[RaceId.EidolonConclave]),
    [RaceId.AstralLeviathan]: buildRaceBlueprints(ABYSSAL_BASE, RACE_SIZE_BONUS[RaceId.AstralLeviathan]),
    [RaceId.SingularitySovereign]: buildRaceBlueprints(ABYSSAL_BASE, RACE_SIZE_BONUS[RaceId.SingularitySovereign]),
};
//# sourceMappingURL=ship-blueprints.js.map